import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  MegaphoneIcon,
  MenuIcon,
  MoonIcon,
  PlusIcon,
  SearchIcon,
  SunIcon,
} from 'lucide-react';
import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover.js';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import {
  isUnread,
  useLastSeenPublishedAt,
} from '@/features/whats-new/seen-store.js';
import { WhatsNewPanel } from '@/features/whats-new/WhatsNewDialog.js';
import { useBreakpoint } from '@/hooks/useMediaQuery.js';
import { useServerEvent } from '@/integration/runtime/server-events.js';
import {
  newestPublishedAt,
  releaseNotesQueryOptions,
} from '@/lib/api/release-notes.js';
import { getRuntimeConfig } from '@/lib/runtime-config.js';
import { cn } from '@/lib/utils.js';
import { useTheme } from '@/ui/theme/ThemeProvider.js';
import { useThemeTransition } from '@/ui/theme/useThemeTransition.js';
import { NotificationsToggle } from '@/features/web-push/NotificationsToggle.js';

const IS_MAC =
  typeof navigator !== 'undefined' &&
  // @ts-expect-error userAgentData is not yet in all TS lib typings
  (/macOS/i.test(navigator.userAgentData?.platform ?? '') ||
    /Mac|iPhone|iPad|iPod/.test(navigator.platform));

/**
 * Top header — sticky chrome above every route.
 *
 * Three-column grid layout (left | center | right) so the search trigger
 * is genuinely centred regardless of how many actions sit on the right.
 *
 * - Left: hamburger (mobile/tablet only).
 * - Centre: ⌘K search trigger ("Search anything").
 * - Right: New chat CTA, "What's new" release notes (unread dot when the
 *   newest published note is unseen), theme toggle. Online/Offline status is
 *   shown only on the sidebar logo's status dot — duplicate pill removed.
 */
export function Header(): JSX.Element {
  const breakpoint = useBreakpoint();
  const setMobileDrawerOpen = useUiStore((s) => s.setMobileDrawerOpen);
  const setCommandPaletteOpen = useUiStore((s) => s.setCommandPaletteOpen);
  const theme = useTheme();
  const { triggerTransition, curtainPortal } = useThemeTransition(
    theme.setMode,
  );

  const isMobile = breakpoint === 'mobile';

  const qc = useQueryClient();

  const showReleaseNotes = getRuntimeConfig().features.releaseNotes;
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const { data: releaseNotes } = useQuery({
    ...releaseNotesQueryOptions(),
    enabled: showReleaseNotes,
  });
  const lastSeenReleaseNote = useLastSeenPublishedAt();
  const hasUnreadReleaseNotes = isUnread(
    newestPublishedAt(releaseNotes),
    lastSeenReleaseNote,
  );
  useServerEvent('release-note.published', () => {
    void qc.invalidateQueries({ queryKey: ['release-notes'] });
  });

  return (
    <header className="sticky top-0 z-30 shrink-0 px-4 pb-2 pt-4 sm:px-6 max-md:py-3">
      <div className="relative flex h-9 max-md:h-11 items-center">
        <div className="relative z-10 flex items-center gap-2">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="size-11 rounded-full text-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
              onClick={() => setMobileDrawerOpen(true)}
              aria-label="Open navigation"
            >
              <MenuIcon className="size-5" />
            </Button>
          )}
        </div>

        {!isMobile && (
          <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-center px-[8.75rem]">
            <button
              type="button"
              onClick={() => setCommandPaletteOpen(true)}
              className={cn(
                'pointer-events-auto flex h-10 w-full max-w-xl items-center gap-2 rounded-full border border-border bg-card/80 px-4 text-sm text-muted-foreground shadow-elev-1 backdrop-blur-md transition-colors',
                'hover:border-foreground/20 hover:bg-card',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              aria-label="Open command palette"
            >
              <SearchIcon className="size-4 shrink-0" />
              <span className="flex-1 truncate text-left">
                Search anything…
              </span>
              <kbd className="hidden items-center gap-0.5 rounded border border-border bg-background/80 px-1.5 py-0.5 font-mono text-mini font-medium tracking-widest sm:inline-flex">
                {IS_MAC ? '⌘K' : 'Ctrl+K'}
              </kbd>
            </button>
          </div>
        )}

        <div className="relative z-10 ml-auto flex items-center gap-2">
          {isMobile && (
            <Link
              to="/home"
              aria-label="New chat"
              className="flex size-11 items-center justify-center rounded-full text-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
            >
              <PlusIcon className="size-5" strokeWidth={2} />
            </Link>
          )}

          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="size-11 rounded-full text-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground"
              onClick={() => setCommandPaletteOpen(true)}
              aria-label="Open command palette"
            >
              <SearchIcon className="size-5" />
            </Button>
          )}

          <NotificationsToggle />

          {showReleaseNotes && (
            <Popover open={whatsNewOpen} onOpenChange={setWhatsNewOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={
                    hasUnreadReleaseNotes
                      ? "What's new — unread updates available"
                      : "What's new"
                  }
                  className="relative size-10 rounded-full text-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground max-md:size-11"
                >
                  <MegaphoneIcon className="size-[var(--icon-lg)] max-md:size-5" />
                  {hasUnreadReleaseNotes && (
                    <span
                      aria-hidden="true"
                      className="absolute right-2 top-2 size-2 rounded-full bg-primary"
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                sideOffset={8}
                className="w-[26rem] max-w-[calc(100vw-1rem)] overflow-hidden p-0"
              >
                <WhatsNewPanel onClose={() => setWhatsNewOpen(false)} />
              </PopoverContent>
            </Popover>
          )}

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  triggerTransition(
                    theme.resolvedMode === 'dark' ? 'light' : 'dark',
                  )
                }
                aria-label="Toggle theme"
                className="size-10 rounded-full text-foreground/80 transition-colors hover:bg-foreground/[0.06] hover:text-foreground max-md:size-11 -mr-2 sm:-mr-3"
              >
                {theme.resolvedMode === 'dark' ? (
                  <SunIcon className="size-[var(--icon-lg)] max-md:size-5" />
                ) : (
                  <MoonIcon className="size-[var(--icon-lg)] max-md:size-5" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Switch to {theme.resolvedMode === 'dark' ? 'light' : 'dark'} mode
            </TooltipContent>
          </Tooltip>
          {curtainPortal}
        </div>
      </div>
    </header>
  );
}
